import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Camera, Mic } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LiveCameraCapture } from "@/components/mobile/LiveCameraCapture";
import { toast } from "sonner";

export default function FieldModeCapture() {
  const navigate = useNavigate();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [selectedDamageType, setSelectedDamageType] = useState<string | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);

  const damageTypes = [
    "Shingles Missing",
    "Hail Damage",
    "Wind Damage",
    "Leak",
    "Flashing Issue",
    "General Wear",
  ];

  const handleOpenCamera = () => {
    if (!selectedDamageType) {
      toast.error("Please select a damage type first");
      return;
    }
    setIsCameraOpen(true);
  };

  const handleCapture = (imageData: string) => {
    setCapturedImages((prev) => [imageData, ...prev]);
    setIsCameraOpen(false);
    toast.success("Photo captured and analyzed!");
  };

  return (
    <>
      {isCameraOpen && (
        <LiveCameraCapture
          onCapture={handleCapture}
          onClose={() => setIsCameraOpen(false)}
          damageType={selectedDamageType || undefined}
        />
      )}

      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-semibold">Field Capture</h1>
            <Button variant="ghost" size="icon">
              <Mic className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Damage Type Selection */}
        <div className="p-4 space-y-3">
          <h2 className="font-semibold text-sm text-muted-foreground">Select Damage Type</h2>
          <div className="grid grid-cols-2 gap-2">
            {damageTypes.map((type) => (
              <Button
                key={type}
                variant={selectedDamageType === type ? "default" : "outline"}
                onClick={() => setSelectedDamageType(type)}
                className="h-auto py-3"
              >
                {type}
              </Button>
            ))}
          </div>
        </div>

        {/* Open Camera Button */}
        <div className="px-4">
          <Button
            onClick={handleOpenCamera}
            disabled={!selectedDamageType}
            size="lg"
            className="w-full h-16 text-lg gap-3"
          >
            <Camera className="h-6 w-6" />
            Open Live Scanner
          </Button>
        </div>

      {/* Job Selection */}
      <Card className="mx-4 mb-4 p-4">
        <h3 className="font-semibold mb-2">Link to Job (Optional)</h3>
        <Button variant="outline" className="w-full">
          Select Job
        </Button>
      </Card>

        {/* Gallery Preview Strip */}
        <div className="px-4 pb-4">
          <h3 className="font-semibold text-sm text-muted-foreground mb-2">
            Captured Photos ({capturedImages.length})
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {capturedImages.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">No photos captured yet</div>
            ) : (
              capturedImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`Capture ${i + 1}`}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border-2 border-border"
                />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
